import React from 'react';
import styled from 'styled-components';
import { webColors as c, fonts } from '../../theme';

/**
 * An abstract, code-drawn PCB/motherboard visual for the Web / IT landing
 * panel. Pure SVG + CSS — no images, no canvas/WebGL. Three layers:
 *
 *  1. Ambient traces  — always faintly visible, static (base texture).
 *  2. Detail layer    — chip geometry, pins, extra traces + pulsing nodes,
 *                        dimmed by default and revealed near the cursor via
 *                        a radial mask driven by the same --mx/--my custom
 *                        properties the panel's spotlight already sets.
 *  3. Micro labels     — tiny technical texture, never competing with copy.
 *
 * Only 2–3 traces/nodes animate at any time, by design — most of the board
 * stays still so the occasional pulse reads as a deliberate signal.
 */

const Wrap = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
`;

const AmbientSvg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0.16;
`;

const DetailLayer = styled.div`
  position: absolute;
  inset: -5%;
  width: 110%;
  height: 110%;
  opacity: ${({ $revealed }) => ($revealed ? 0.55 : 0.24)};
  transition: opacity 0.5s ease;
  mask-image: radial-gradient(circle 320px at var(--mx, 50%) var(--my, 50%), black 0%, black 35%, transparent 78%);
  -webkit-mask-image: radial-gradient(circle 320px at var(--mx, 50%) var(--my, 50%), black 0%, black 35%, transparent 78%);

  @media (max-width: 720px) {
    mask-image: none;
    -webkit-mask-image: none;
    opacity: 0.16;
  }
`;

const DetailSvg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;

  .pulse-trace {
    stroke-dasharray: 5 340;
    animation: dash 6s linear infinite;
  }
  .pulse-trace:nth-of-type(2) { animation-delay: -2s; }
  .pulse-trace:nth-of-type(3) { animation-delay: -4s; }

  .pulse-node { animation: glow 4.5s ease-in-out infinite; }
  .pulse-node:nth-of-type(2) { animation-delay: 1.2s; }
  .pulse-node:nth-of-type(3) { animation-delay: 2.6s; }

  @keyframes dash { to { stroke-dashoffset: -690; } }
  @keyframes glow {
    0%, 100% { opacity: 0.25; }
    50% { opacity: 0.95; }
  }

  @media (max-width: 720px) {
    .pulse-trace, .pulse-node { animation: none; }
  }

  @media (prefers-reduced-motion: reduce) {
    .pulse-trace, .pulse-node { animation: none; }
  }
`;

const ChipRect = styled.rect`
  fill: none;
  stroke: ${c.borderHover};
  stroke-width: 1;
  transition: stroke 0.4s ease, filter 0.4s ease;

  ${DetailLayer}:hover &,
  [data-hot='true'] & {
    stroke: ${c.accent};
    filter: drop-shadow(0 0 5px ${c.accentGlow});
  }
`;

const Label = styled.span`
  position: absolute;
  font-family: ${fonts.mono};
  font-size: 0.62rem;
  letter-spacing: 0.08em;
  color: ${c.accent};
  opacity: 0.16;
  user-select: none;
  pointer-events: none;

  @media (max-width: 720px) {
    display: none;
  }
`;

const labels = [
  { text: 'SYS', top: '10%', left: '8%' },
  { text: 'DEV_01', top: '18%', right: '10%' },
  { text: '</>', bottom: '30%', left: '12%' },
  { text: 'BUILD', bottom: '14%', right: '16%' },
  { text: '0x2F', top: '46%', right: '6%' },
];

// Pin ticks generated deterministically around the "processor" rect.
function pins(x, y, w, h) {
  const out = [];
  const step = w / 4;
  for (let i = 1; i < 4; i += 1) {
    out.push(`M${x + i * step},${y} V${y - 10}`); // top
    out.push(`M${x + i * step},${y + h} V${y + h + 10}`); // bottom
  }
  const stepV = h / 3;
  for (let i = 1; i < 3; i += 1) {
    out.push(`M${x},${y + i * stepV} H${x - 10}`); // left
    out.push(`M${x + w},${y + i * stepV} H${x + w + 10}`); // right
  }
  return out;
}

const chip = { x: 118, y: 200, w: 84, h: 84 };
const pinPaths = pins(chip.x, chip.y, chip.w, chip.h);

function CircuitBoard({ revealed = false, hot = false }) {
  return (
    <Wrap aria-hidden="true" data-hot={hot}>
      <AmbientSvg viewBox="0 0 320 520" preserveAspectRatio="xMidYMid slice">
        <path d="M-10,70 H90 V150 H230 V50 H330" fill="none" stroke={c.accent} strokeWidth="1" />
        <path d="M330,190 H210 V270 H70 V350" fill="none" stroke={c.accent} strokeWidth="1" />
        <path d="M-10,430 H130 V490 H330" fill="none" stroke={c.accent} strokeWidth="1" />
        <path d="M20,260 H90" fill="none" stroke={c.accent} strokeWidth="1" />
        <path d="M260,340 V430 H180" fill="none" stroke={c.accent} strokeWidth="1" />
      </AmbientSvg>

      <DetailLayer $revealed={revealed}>
        <DetailSvg viewBox="0 0 320 520" preserveAspectRatio="xMidYMid slice">
          <path className="pulse-trace" d="M-10,70 H90 V150 H230 V50 H330" fill="none" stroke={c.accentLight} strokeWidth="1.2" />
          <path d="M330,190 H210 V270 H70 V350" fill="none" stroke={c.borderHover} strokeWidth="1" />
          <path className="pulse-trace" d="M-10,430 H130 V490 H330" fill="none" stroke={c.accentLight} strokeWidth="1.2" />
          <path d="M20,260 H90" fill="none" stroke={c.borderHover} strokeWidth="1" />
          <path className="pulse-trace" d="M260,340 V430 H180" fill="none" stroke={c.accentLight} strokeWidth="1.2" />
          <path d={`M${chip.x + chip.w},${chip.y + 20} H260 V90`} fill="none" stroke={c.borderHover} strokeWidth="1" />
          <path d={`M${chip.x},${chip.y + 60} H50 V400`} fill="none" stroke={c.borderHover} strokeWidth="1" />

          {pinPaths.map((d, i) => (
            <path key={i} d={d} fill="none" stroke={c.borderHover} strokeWidth="1" />
          ))}
          <ChipRect x={chip.x} y={chip.y} width={chip.w} height={chip.h} rx="5" />

          <circle className="pulse-node" cx="90" cy="150" r="2.6" fill={c.accentLight} />
          <circle className="pulse-node" cx="230" cy="50" r="2.6" fill={c.accentLight} />
          <circle className="pulse-node" cx="130" cy="490" r="2.6" fill={c.accentLight} />
          <circle cx="210" cy="270" r="2.2" fill={c.borderHover} />
          <circle cx="70" cy="350" r="2.2" fill={c.borderHover} />
          <circle cx="180" cy="430" r="2.2" fill={c.borderHover} />
        </DetailSvg>
      </DetailLayer>

      {labels.map(({ text, ...pos }) => (
        <Label key={text} style={pos}>{text}</Label>
      ))}
    </Wrap>
  );
}

export default CircuitBoard;

